import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTimezone } from "@/contexts/TimezoneContext";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { logger } from '@/lib/logger';

// Common timezones with user-friendly names (removed UTC as it's confusing)
const commonTimezones = [
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
];

export const TimezoneSelector = () => {
  const { user } = useAuth();
  const { timezone, setTimezone } = useTimezone();
  const [isLoading, setIsLoading] = useState(false);

  const handleTimezoneChange = async (newTimezone: string) => {
    setIsLoading(true);
    try {
      await setTimezone(newTimezone);
      toast({
        title: "Timezone updated",
        description: `Display timezone set to ${getTimezoneLabel(newTimezone)}${!user ? '. Sign in to save this preference.' : ''}`,
      });
    } catch (error) {
      logger.error('Failed to update timezone:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update timezone preference.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTimezoneLabel = (timezone: string): string => {
    const found = commonTimezones.find(tz => tz.value === timezone);
    return found ? found.label : timezone;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="text-foreground hover:text-primary">
          <Clock className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">
            {getTimezoneLabel(timezone).split(' ')[0]}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end">
        <DropdownMenuLabel>Display Timezone</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {commonTimezones.map((tz) => (
          <DropdownMenuItem
            key={tz.value}
            onClick={() => handleTimezoneChange(tz.value)}
            disabled={isLoading}
            className={timezone === tz.value ? "bg-accent" : ""}
          >
            <div className="flex flex-col">
              <span className="font-medium">{tz.label}</span>
              <span className="text-xs text-muted-foreground">
                {new Date().toLocaleTimeString('en-US', {
                  timeZone: tz.value,
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZoneName: 'short'
                })}
              </span>
            </div>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {user ? 'Preference saved to your account' : 'Sign in to save preference'}
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};